import {
  Grid, Stack, Group, Button, Text, Anchor, Code, Table,
  Card, Divider, Menu, Modal, Select, Textarea, ActionIcon, Tooltip, Skeleton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconNote, IconChevronDown } from '@tabler/icons-react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { cardType, type CaseStatus, type ClosedReason, type Trip, type TripStatus } from '@taksikortti/shared';
import {
  useApplyAction, useCaseDetail, useApplyTripAction,
  type CaseAction, type TripAction,
} from '../api/cases';
import { StatusBadge } from '../components/StatusBadge';
import { CardTypeBadge } from '../components/CardTypeBadge';
import { InvoiceBadge } from '../components/InvoiceBadge';
import { ErrorState } from '../components/ErrorState';
import { ApiError } from '../api/client';
import { t } from '../lib/strings';
import { eur } from '../lib/format';
import { formatDateShort, formatDateTime, formatRelative } from '../lib/dates';

export function CaseDetailPage() {
  const params = useParams({ from: '/cases/$korttinumero/$caseId' });
  const navigate = useNavigate();
  const detail = useCaseDetail(params.korttinumero, params.caseId);
  const caseMutation = useApplyAction(params.korttinumero, params.caseId);
  const tripMutation = useApplyTripAction(params.korttinumero, params.caseId);
  const [closeTarget, setCloseTarget] = useState<Trip | null>(null);
  const [closeModalOpen, { open: openCloseModal, close: closeCloseModal }] = useDisclosure(false);
  const [notesTarget, setNotesTarget] = useState<Trip | null>(null);
  const [notesModalOpen, { open: openNotesModal, close: closeNotesModal }] = useDisclosure(false);

  if (detail.isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={24} width={180} />
        <Skeleton height={40} width={280} />
        <Skeleton height={320} radius="md" />
      </Stack>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Stack gap="md">
        <Anchor component={Link} to="/cases">{t.caseDetail.backToList}</Anchor>
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      </Stack>
    );
  }

  const { case: c, etag, trips, aggregates, timeline, previousIncidents } = detail.data;
  const status = c.status as CaseStatus;

  function runTripAction(dedupKey: string, action: TripAction): void {
    tripMutation.mutate(
      { dedupKey, action },
      {
        onError: (err) => {
          if (err instanceof ApiError) {
            if (err.body.code === 'stale_version') {
              notifications.show({ color: 'orange', message: t.caseDetail.staleToast, autoClose: false });
              return;
            }
            if (err.body.code === 'illegal_transition') {
              notifications.show({ color: 'red', message: t.errors.illegalTransition });
              return;
            }
          }
          notifications.show({ color: 'red', message: t.errors.generic });
        },
        onSuccess: (r) => {
          notifications.show({
            color: 'green',
            message: r.caseClosed ? 'Tapaus sulkeutui automaattisesti.' : t.caseDetail.actionDone,
          });
        },
      },
    );
  }

  function runCaseAction(action: CaseAction): void {
    caseMutation.mutate(
      { etag, action },
      {
        onError: (err) => {
          if (err instanceof ApiError && err.body.code === 'stale_version') {
            notifications.show({ color: 'orange', message: t.caseDetail.staleToast, autoClose: false });
            return;
          }
          notifications.show({ color: 'red', message: t.errors.generic });
        },
        onSuccess: () => {
          // edit_notes has its own inline "Tallennettu" flash — don't double up.
          if (action.kind !== 'edit_notes') notifications.show({ color: 'green', message: t.caseDetail.actionDone });
        },
      },
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Anchor component={Link} to="/cases">{t.caseDetail.backToList}</Anchor>
        <CardTypeBadge type={cardType(params.korttinumero)} />
      </Group>

      <Group gap="sm" align="center" wrap="nowrap">
        <Code fz="lg" fw={700}>{params.korttinumero}</Code>
        <StatusBadge status={status} closedReason={c.closedReason as ClosedReason | null} />
        {/* Customer name — denormalised on the case; older migrated rows fall back to the trips. */}
        <Text fw={600} size="lg" truncate>
          {c.yritys ?? trips.find((tr) => tr.asiakas)?.asiakas ?? '—'}
        </Text>
      </Group>

      <Grid>
        {/* Left pane — minimal case summary */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder>
            <Stack gap="sm">
              <Text size="sm" fw={500}>Tämä tapaus</Text>
              <Text size="sm">
                {aggregates.billable} veloitusta · {aggregates.wrongRemaining} väärä jäljellä
                {aggregates.refund > 0 && ` · ${aggregates.refund} hyvitystä`}
                {aggregates.eiLaskuteta > 0 && ` · ${aggregates.eiLaskuteta} ei laskuteta`}
                {aggregates.closedTrip > 0 && ` · ${aggregates.closedTrip} käsitelty`}
              </Text>
              <FieldRow label="Ensim. veloitus" value={formatDateTime(aggregates.firstCharge)} />
              <FieldRow label="Viim. veloitus" value={formatDateTime(aggregates.lastCharge)} />
              <Divider />
              <Text size="xs" c="dimmed">
                Tapauksen tila päivittyy automaattisesti yksittäisten matkojen tilojen mukaan.
              </Text>
              {status !== 'closed' && (
                // Case-level close (kertis-style) — the only way to resolve a case
                // whose trips are all non-billable (e.g. blocked repeats that were
                // still settled) since those rows carry no per-trip actions.
                <Menu shadow="md">
                  <Menu.Target>
                    <Button variant="light" loading={caseMutation.isPending} rightSection={<IconChevronDown size={14} />}>
                      {t.caseDetail.closeCase}
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={() => runCaseAction({ kind: 'close', reason: 'resolved' })}>
                      {t.closedReason.resolved}
                    </Menu.Item>
                    <Menu.Item onClick={() => runCaseAction({ kind: 'close', reason: 'no_reply' })}>
                      {t.closedReason.no_reply}
                    </Menu.Item>
                    <Menu.Item onClick={() => runCaseAction({ kind: 'close', reason: 'not_applicable' })}>
                      {t.closedReason.not_applicable}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
              {status === 'closed' && (
                <Button
                  variant="light"
                  onClick={() => runCaseAction({ kind: 'reopen' })}
                  loading={caseMutation.isPending}
                >
                  {t.caseDetail.reopen}
                </Button>
              )}
              <Divider />
              <CaseNotesEditor
                key={c.caseId + (c.notes ?? '')}
                initialNotes={c.notes ?? ''}
                onSave={(notes) => runCaseAction({ kind: 'edit_notes', notes })}
                saving={caseMutation.isPending}
              />
            </Stack>
          </Card>
        </Grid.Col>

        {/* Right pane — trips + previous incidents + timeline */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <Card withBorder>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={500}>{t.caseDetail.trips}</Text>
                  <Text size="sm" c="dimmed">
                    {t.caseDetail.tripsHeader(aggregates.billable, aggregates.refund, aggregates.eiLaskuteta, aggregates.closedTrip)}
                  </Text>
                </Group>
                <Table.ScrollContainer minWidth={720}>
                  <Table withTableBorder fz="xs">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Aika</Table.Th>
                        <Table.Th>Kuitti</Table.Th>
                        <Table.Th ta="right">Hinta</Table.Th>
                        <Table.Th>Laskutus</Table.Th>
                        <Table.Th>Tilitys</Table.Th>
                        <Table.Th>Tila</Table.Th>
                        <Table.Th>Kuljettaja</Table.Th>
                        <Table.Th>Auto</Table.Th>
                        <Table.Th>Palvelutunnus</Table.Th>
                        <Table.Th>Toiminnot</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {trips.map((tr) => (
                        <TripRow
                          key={tr.dedupKey}
                          trip={tr}
                          loading={tripMutation.isPending}
                          onAction={(action) => runTripAction(tr.dedupKey, action)}
                          onCloseClick={() => { setCloseTarget(tr); openCloseModal(); }}
                          onNotesClick={() => { setNotesTarget(tr); openNotesModal(); }}
                        />
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Stack>
            </Card>

            {previousIncidents.length > 0 && (
              <Card withBorder>
                <Stack gap="xs">
                  <Text fw={500}>{t.caseDetail.previousIncidents}</Text>
                  {previousIncidents.map((p) => (
                    <Group key={p.caseId} justify="space-between" gap="xs">
                      <Group gap="xs">
                        <StatusBadge status={p.status} closedReason={p.closedReason} />
                        <Text size="sm">
                          {p.closedAt ? formatDateShort(p.closedAt) : formatDateShort(p.createdAt)}
                        </Text>
                        <Text size="xs" c="dimmed">{p.notesPreview}</Text>
                      </Group>
                      <Anchor
                        component="button"
                        size="sm"
                        onClick={() => void navigate({
                          to: '/cases/$korttinumero/$caseId',
                          params: { korttinumero: params.korttinumero, caseId: p.caseId },
                        })}
                      >
                        {t.worklist.openCase}
                      </Anchor>
                    </Group>
                  ))}
                </Stack>
              </Card>
            )}

            <Card withBorder>
              <Stack gap="xs">
                <Text fw={500}>{t.caseDetail.timeline}</Text>
                {timeline.length === 0 ? (
                  <Text size="sm" c="dimmed">{t.dupDetail.noEvents}</Text>
                ) : (
                  timeline.map((e) => (
                    <Group key={e.inverseTimestamp} gap="sm">
                      <Text size="sm" c="dimmed">{formatRelative(e.at)}</Text>
                      <Text size="sm">
                        <b>{e.actor}</b> — {describeEvent(e.kind, e.fromStatus, e.toStatus, e.detailsJson)}
                      </Text>
                    </Group>
                  ))
                )}
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      <CloseTripModal
        opened={closeModalOpen}
        trip={closeTarget}
        onClose={() => { closeCloseModal(); setCloseTarget(null); }}
        onConfirm={(reason, notes) => {
          if (!closeTarget) return;
          runTripAction(closeTarget.dedupKey, { kind: 'close', reason, notes });
          closeCloseModal();
          setCloseTarget(null);
        }}
        loading={tripMutation.isPending}
      />

      <TripNotesModal
        opened={notesModalOpen}
        trip={notesTarget}
        onClose={() => { closeNotesModal(); setNotesTarget(null); }}
        onConfirm={(notes) => {
          if (!notesTarget) return;
          runTripAction(notesTarget.dedupKey, { kind: 'edit_notes', notes });
          closeNotesModal();
          setNotesTarget(null);
        }}
        loading={tripMutation.isPending}
      />
    </Stack>
  );
}

function CaseNotesEditor({
  initialNotes, onSave, saving,
}: {
  initialNotes: string;
  onSave: (notes: string) => void;
  saving: boolean;
}) {
  const [notes, setNotes] = useState(initialNotes);
  const [savedFlash, setSavedFlash] = useState(false);
  const dirty = notes !== initialNotes;

  useEffect(() => {
    if (!saving && !dirty && savedFlash) {
      const timer = setTimeout(() => setSavedFlash(false), 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [saving, dirty, savedFlash]);

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>{t.caseDetail.caseNotes}</Text>
      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
        placeholder={t.caseDetail.caseNotesPlaceholder}
        aria-label={t.caseDetail.caseNotes}
        autosize
        minRows={3}
      />
      <Group justify="flex-end" gap="xs">
        {savedFlash && !dirty && (
          <Text size="xs" c="green">{t.caseDetail.caseNotesSaved}</Text>
        )}
        <Button
          size="compact-sm"
          variant="light"
          disabled={!dirty}
          loading={saving && dirty}
          onClick={() => { onSave(notes); setSavedFlash(true); }}
        >
          {t.caseDetail.caseNotesSave}
        </Button>
      </Group>
    </Stack>
  );
}

function TripRow({
  trip, loading, onAction, onCloseClick, onNotesClick,
}: {
  trip: Trip;
  loading: boolean;
  onAction: (action: TripAction) => void;
  onCloseClick: () => void;
  onNotesClick: () => void;
}) {
  const hinta = trip.hinta ?? 0;
  const isRefund = hinta < 0;
  const isEi = trip.laskutustila === 'Ei laskuteta';
  const isBillable = hinta > 0 && !isEi;
  const tripStatus = (trip.tripStatus ?? 'open') as TripStatus;
  const rowOpacity = !isBillable ? 0.6 : tripStatus === 'closed' ? 0.55 : 1;
  const hasNotes = Boolean(trip.tripNotes && trip.tripNotes.trim().length > 0);

  return (
    <Table.Tr opacity={rowOpacity}>
      <Table.Td>{formatDateTime(trip.matkanAlkamisaika)}</Table.Td>
      <Table.Td>{trip.kuittinumero}</Table.Td>
      <Table.Td ta="right">{eur(hinta)} €</Table.Td>
      <Table.Td><InvoiceBadge status={trip.laskutustila} /></Table.Td>
      <Table.Td><InvoiceBadge status={trip.tilitystila} /></Table.Td>
      <Table.Td>
        {isBillable ? (
          <StatusBadge status={tripStatus} closedReason={trip.closedReason} />
        ) : isRefund ? (
          <Text size="xs" c="dimmed">hyvitys</Text>
        ) : isEi ? (
          <Text size="xs" c="dimmed">ei laskuteta</Text>
        ) : (
          <Text size="xs" c="dimmed">—</Text>
        )}
      </Table.Td>
      <Table.Td>{trip.kuljettaja ?? '—'}</Table.Td>
      <Table.Td>{trip.autonumero ?? '—'}</Table.Td>
      <Table.Td>{trip.palvelutunnus ? <Code>{trip.palvelutunnus}</Code> : '—'}</Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          {isBillable && (
            <TripActionsCell
              status={tripStatus}
              loading={loading}
              onAction={onAction}
              onCloseClick={onCloseClick}
            />
          )}
          <Tooltip label={hasNotes ? trip.tripNotes : t.caseDetail.tripNotesEdit} multiline maw={300}>
            <ActionIcon
              size="sm"
              variant={hasNotes ? 'light' : 'subtle'}
              color={hasNotes ? 'blue' : 'gray'}
              onClick={onNotesClick}
              aria-label={t.caseDetail.tripNotesTitle}
            >
              <IconNote size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function TripNotesModal({
  opened, trip, onClose, onConfirm, loading,
}: {
  opened: boolean;
  trip: Trip | null;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  loading: boolean;
}) {
  const [notes, setNotes] = useState('');
  // Reset textarea contents when the modal opens against a new trip.
  useEffect(() => {
    if (opened) setNotes(trip?.tripNotes ?? '');
  }, [opened, trip?.dedupKey, trip?.tripNotes]);
  return (
    <Modal opened={opened} onClose={onClose} title={t.caseDetail.tripNotesTitle} centered>
      <Stack gap="sm">
        {trip && (
          <Text size="sm" c="dimmed">
            Kuitti {trip.kuittinumero} · {trip.kuljettaja ?? '—'} · {eur(trip.hinta)} €
          </Text>
        )}
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          placeholder={t.caseDetail.tripNotesPlaceholder}
          aria-label={t.caseDetail.tripNotesTitle}
          autosize
          minRows={4}
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose} disabled={loading}>
            {t.caseDetail.tripNotesCancel}
          </Button>
          <Button onClick={() => onConfirm(notes)} loading={loading}>
            {t.caseDetail.tripNotesSave}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function TripActionsCell({
  status, loading, onAction, onCloseClick,
}: {
  status: TripStatus;
  loading: boolean;
  onAction: (action: TripAction) => void;
  onCloseClick: () => void;
}) {
  // Primary action mirrors case workflow: open → send_query → send_reminder → close.
  // Closed exposes Reopen.
  switch (status) {
    case 'open':
      return (
        <Group gap={4} wrap="nowrap">
          <Button size="compact-xs" variant="light" loading={loading}
            onClick={() => onAction({ kind: 'send_query' })}>
            {t.caseDetail.tripActions.send_query}
          </Button>
          <Button size="compact-xs" variant="subtle" color="gray" loading={loading}
            onClick={onCloseClick}>
            {t.caseDetail.tripActions.close}
          </Button>
        </Group>
      );
    case 'query_sent':
      return (
        <Group gap={4} wrap="nowrap">
          <Button size="compact-xs" variant="light" loading={loading}
            onClick={() => onAction({ kind: 'send_reminder' })}>
            {t.caseDetail.tripActions.send_reminder}
          </Button>
          <Button size="compact-xs" variant="subtle" color="gray" loading={loading}
            onClick={onCloseClick}>
            {t.caseDetail.tripActions.close}
          </Button>
        </Group>
      );
    case 'reminder_sent':
      return (
        <Menu shadow="md">
          <Menu.Target>
            <Button size="compact-xs" variant="light" loading={loading} rightSection={<IconChevronDown size={14} />}>
              {t.caseDetail.tripActions.close}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => onAction({ kind: 'close', reason: 'resolved' })}>
              {t.closedReason.resolved}
            </Menu.Item>
            <Menu.Item onClick={() => onAction({ kind: 'close', reason: 'no_reply' })}>
              {t.closedReason.no_reply}
            </Menu.Item>
            <Menu.Item onClick={() => onAction({ kind: 'close', reason: 'not_applicable' })}>
              {t.closedReason.not_applicable}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      );
    case 'closed':
      return (
        <Button size="compact-xs" variant="subtle" loading={loading}
          onClick={() => onAction({ kind: 'reopen' })}>
          {t.caseDetail.tripActions.reopen}
        </Button>
      );
  }
}

function CloseTripModal({
  opened, trip, onClose, onConfirm, loading,
}: {
  opened: boolean;
  trip: Trip | null;
  onClose: () => void;
  onConfirm: (reason: ClosedReason, notes: string) => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState<ClosedReason>('resolved');
  const [notes, setNotes] = useState('');
  return (
    <Modal opened={opened} onClose={onClose} title={t.caseDetail.closeTripTitle} centered>
      <Stack gap="sm">
        {trip && (
          <Text size="sm" c="dimmed">
            Kuitti {trip.kuittinumero} · {trip.kuljettaja ?? '—'} · {eur(trip.hinta)} €
          </Text>
        )}
        <Select
          label={t.caseDetail.closeTripReason}
          value={reason}
          onChange={(v) => setReason((v as ClosedReason) ?? 'resolved')}
          data={[
            { value: 'resolved', label: t.closedReason.resolved },
            { value: 'no_reply', label: t.closedReason.no_reply },
            { value: 'not_applicable', label: t.closedReason.not_applicable },
          ]}
          allowDeselect={false}
        />
        <Textarea
          label={t.caseDetail.closeTripNotes}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={3}
          autosize
        />
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose} disabled={loading}>
            {t.caseDetail.closeTripCancel}
          </Button>
          <Button onClick={() => onConfirm(reason, notes)} loading={loading}>
            {t.caseDetail.closeTripSave}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm">{value}</Text>
    </Group>
  );
}

function describeEvent(
  kind: string,
  fromStatus: CaseStatus | null,
  toStatus: CaseStatus | null,
  detailsJson: string,
): string {
  switch (kind) {
    case 'status_changed':
      if (fromStatus === null && toStatus) return `tapaus avattu (${t.status[toStatus]})`;
      if (fromStatus && toStatus) return `${t.status[fromStatus]} → ${t.status[toStatus]}`;
      return 'tilanmuutos';
    case 'reopened': return 'avasi uudelleen';
    case 'migrated': return 'tuotu Excelistä';
    case 'trip_status_changed': {
      const d = safeParseJson(detailsJson);
      const from = d?.from ? t.caseDetail.tripStatus[d.from as TripStatus] ?? String(d.from) : '?';
      const to = d?.to ? t.caseDetail.tripStatus[d.to as TripStatus] ?? String(d.to) : '?';
      return `matka: ${from} → ${to}`;
    }
    case 'trip_closed': {
      const d = safeParseJson(detailsJson);
      const reason = d?.reason ? t.closedReason[d.reason as ClosedReason] ?? String(d.reason) : '';
      return `sulki matkan${reason ? ` (${reason})` : ''}`;
    }
    case 'trip_reopened': return 'avasi matkan uudelleen';
    case 'auto_closed': return 'tapaus sulkeutui automaattisesti';
    case 'flagged': return 'merkitsi lipulla';
    case 'unflagged': return 'poisti lipun';
    case 'note_added': return 'lisäsi muistiinpanon';
    case 'reassigned': return 'vaihtoi vastuuhenkilön';
    default: return 'tapahtuma';
  }
}

function safeParseJson(s: string): Record<string, unknown> | null {
  try { return JSON.parse(s) as Record<string, unknown>; } catch { return null; }
}
